import { Form, Formik } from "formik";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import Button from "../../../../atoms/Button";
import jsPDF from "jspdf";
import { CREATE_MEETING } from "../../../../../api/apiReuniones";
import { UPLOAD_FILE } from "../../../../../api/apiFiles";
import { SEND_EMAIL } from "../../../../../api/apiEmails";
import DynamicTasksField from "./DynamicTasksField";
import useModal from "../../../../../hooks/useModal";
import Modal from "../../../../molecules/Modal";
import ModalCreateExAs from "../../asistentesExternos/ModalCreateExAs";
import MeetingHeader from "./MeetingHeader";
import MeetingMembersSelect from "./MeetingMembersSelect";
import PhotoCapture from "./PhotoCapture";
import MeetingFormFields from "../../../../molecules/empresas/reuniones/MeetingFormFields";
import FileInputsUploader from "./FilesInputUploader";
import useFetchMeetingData from "../../../../../hooks/useFetchMeetingData";
import CanvasUploader from "./CanvasUploader";
import useMeetingRealtime from "../../../../../hooks/useMeetingsRealtime";
import { socket } from "../../../../../socket/socketClient";

const RemoteFormikSync = ({ remoteValues, setFieldValue, values }) => {
	// --- Actualizar Formik cuando llegan valores remotos ---
	useEffect(() => {
		for (const [field, value] of Object.entries(remoteValues)) {
			if (values[field] !== value) {
				setFieldValue(field, value, false);
			}
		}
	}, [remoteValues, setFieldValue, values]);
	return null;
};
const ModalCreateReunion = ({
	onClose,
	refetch,
	empresa,
	accountants,
	admins,
}) => {
	const [capturedPhotos, setCapturedPhotos] = useState([]);
	const [contador, setContador] = useState();
	const [fileInputs, setFileInputs] = useState([
		{ id: Date.now(), file: null },
	]);
	const [remoteValues, setRemoteValues] = useState({}); // valores remotos
	const webcamRef = useRef(null);
	const canvasUploaderRef = useRef();
	const [showCamera, setShowCamera] = useState(false);
	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const { open, close, isOpen } = useModal();
	const { cliente, usuariosRS, asistentesExt, refreshData } =
		useFetchMeetingData(empresa);

	// callback para actualizar el form al recibir un cambio remoto
	const handleRemoteFieldUpdate = useCallback((field, value) => {
		setRemoteValues((prev) => ({ ...prev, [field]: value }));
	}, []);

	// --- Hook de sincronización en tiempo real ---
	useMeetingRealtime({
		meetingId: empresa?.id,
		setFieldValues: handleRemoteFieldUpdate,
	});

	// --- Determinar contador ---
	useEffect(() => {
		if (!empresa) return;
		if (empresa.accountantId) {
			const accountant = accountants?.find(
				(a) => a.id === empresa.accountantId
			);
			setContador(accountant || null);
		} else if (empresa.adminId) {
			const admin = admins?.find((a) => a.id === empresa.adminId);
			setContador(admin || null);
		} else {
			setContador(null);
		}
	}, [empresa, accountants, admins]);

	const validate = (values) => {
		const errors = {};
		if (!values.body) errors.body = "La reunión es requerida";
		if (!values.subject) errors.subject = "El asunto es requerido";
		return errors;
	};

	const handleEmailSent = async (values) => {
		const members = {
			rs: Array.isArray(values?.membersRS)
				? values.membersRS.map((i) => i.value)
				: [],
			ext: Array.isArray(values?.membersEXT)
				? values.membersEXT.map((i) => i.value)
				: [],
		};
		try {
			await SEND_EMAIL({
				...values,
				members,
				accountantEmail: contador?.user?.email,
				clientEmail: cliente?.user?.email,
			});
			toast.success("Emails enviados");
		} catch (err) {
			console.error("Error al enviar emails:", err);
			toast.error("Error al enviar emails");
		}
	};

	const handleClose = () => {
		refreshData();
		close();
	};

	const handleSubmit = async (values, { setSubmitting }) => {
		let files = [];
		try {
			const isoDate = new Date(values.createdAt).toISOString();

			// --- Canvas PDF ---
			const canvasData = canvasUploaderRef.current;
			if (canvasData?.isIncluded && canvasData.getCanvasRef()) {
				const stage = canvasData.getCanvasRef();
				const dataUrl = stage.toDataURL({ pixelRatio: 2 });
				const pdf = new jsPDF({
					orientation:
						stage.width() > stage.height() ? "landscape" : "portrait",
					unit: "px",
					format: [stage.width(), stage.height()],
				});
				pdf.addImage(dataUrl, "PNG", 0, 0, stage.width(), stage.height());
				const pdfBlob = pdf.output("blob");
				const filename = `reunion-canvas-${Date.now()}.pdf`;
				const uploadRes = await UPLOAD_FILE(
					empresa.id,
					filename,
					"application/pdf",
					"meets/"
				);
				await fetch(uploadRes.url, {
					method: "PUT",
					headers: { "Content-Type": "application/pdf" },
					body: pdfBlob,
				});
				files.push({ type: "canvas", s3Key: uploadRes.s3Key });
			}

			// --- Fotos ---
			for (let i = 0; i < capturedPhotos.length; i++) {
				const res = await fetch(capturedPhotos[i]);
				const blob = await res.blob();
				const filename = `reunion-photo-${Date.now()}-${i}.jpg`;
				const uploadRes = await UPLOAD_FILE(empresa.id, filename, "image/jpeg");
				await fetch(uploadRes.url, {
					method: "PUT",
					headers: { "Content-Type": "image/jpeg" },
					body: blob,
				});
				files.push({ type: "photo", s3Key: uploadRes.s3Key });
			}

			// --- Archivos PDF ---
			for (let i = 0; i < fileInputs.length; i++) {
				const file = fileInputs[i].file;
				if (!file) continue;
				const ext = file.name?.split(".").pop()?.toLowerCase();
				if (ext !== "pdf") {
					toast.error(`El archivo "${file.name}" no es un PDF.`);
					continue;
				}
				const filename = `reunion-file-${Date.now()}-${i}.pdf`;
				const uploadRes = await UPLOAD_FILE(
					empresa.id,
					filename,
					"application/pdf"
				);
				await fetch(uploadRes.url, {
					method: "PUT",
					headers: { "Content-Type": "application/pdf" },
					body: file,
				});
				files.push({ type: "upload", s3Key: uploadRes.s3Key });
			}

			// --- Crear reunión ---
			const members = {
				rs: Array.isArray(values?.membersRS)
					? values.membersRS.map((i) => i.value)
					: [],
				ext: Array.isArray(values?.membersEXT)
					? values.membersEXT.map((i) => i.value)
					: [],
			};
			await CREATE_MEETING({
				...values,
				members,
				createdAt: isoDate,
				clientEmail: cliente?.user?.email,
				accountantEmail: contador?.user?.email,
				files,
			});
			await handleEmailSent(values);
			toast.success("Registro exitoso");
			refetch();
			onClose();
		} catch (err) {
			console.error("Error al registrar:", err);
			toast.error("Error al registrar");
		} finally {
			setSubmitting(false);
		}
	};

	const initialValues = {
		body: [""],
		notes: "",
		subject: "",
		createdAt: "",
		clientId: empresa?.id,
		accountantId: empresa?.accountantId || null,
		adminId: empresa?.adminId || null,
		membersRS: [],
		membersEXT: [],
		location: "",
		files: "",
		client_tasks: [""],
		office_tasks: [""],
		photos: [],
	};

	const options = usuariosRS.map((u) => ({ value: u, label: u.name }));
	const optionsExt = asistentesExt?.map((u) => ({ value: u, label: u?.name }));

	return (
		<div className="w-full">
			<Formik
				initialValues={initialValues}
				onSubmit={handleSubmit}
				validate={validate}
				enableReinitialize
			>
				{({ isSubmitting, setFieldValue, values }) => {
					<RemoteFormikSync
						remoteValues={remoteValues}
						setFieldValue={setFieldValue}
						values={values}
					/>;
					return (
						<Form className="w-full flex flex-col items-center">
							<div className="w-full flex flex-col items-center justify-center text-center space-y-4">
								<MeetingHeader
									empresa={empresa}
									admins={admins}
									accountants={accountants}
									setContador={setContador}
								/>
								<MeetingFormFields
									values={values}
									setFieldValue={(field, value) => {
										setFieldValue(field, value);
										if (socket.connected) {
											socket.emit("update_meeting_field", {
												meetingId: empresa.id,
												field,
												value,
											});
										}
									}}
								/>
								<div className="w-full flex flex-row space-x-4">
									<MeetingMembersSelect
										meetingId={empresa.id}
										optionsRS={options}
										optionsEXT={optionsExt}
										values={values}
										setFieldValue={setFieldValue}
										open={open}
									/>
								</div>
								<DynamicTasksField
									name="body"
									label="Temas tratados en la reunión"
									values={values.body}
									setFieldValue={setFieldValue}
									meetingId={empresa.id}
								/>
								<div className="w-full flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
									<DynamicTasksField
										name="client_tasks"
										label="Tareas para el cliente"
										values={values.client_tasks}
										setFieldValue={setFieldValue}
										meetingId={empresa.id}
									/>
									<DynamicTasksField
										name="office_tasks"
										label="Tareas para el despacho"
										values={values.office_tasks}
										setFieldValue={setFieldValue}
										meetingId={empresa.id}
									/>
								</div>
							</div>

							<CanvasUploader meetingId={empresa.id} ref={canvasUploaderRef} />
							<PhotoCapture
								capturedPhotos={capturedPhotos}
								setCapturedPhotos={setCapturedPhotos}
								webcamRef={webcamRef}
								showCamera={showCamera}
								setShowCamera={setShowCamera}
								isMobile={isMobile}
							/>
							<FileInputsUploader
								fileInputs={fileInputs}
								setFileInputs={setFileInputs}
							/>

							<Button
								text={isSubmitting ? <PropagateLoader color="red" /> : "Enviar"}
								type="submit"
								disabled={isSubmitting}
								bgColor="blue"
							/>
						</Form>
					);
				}}
			</Formik>

			{isOpen && (
				<Modal title="Agregar miembro" onClose={handleClose}>
					<ModalCreateExAs client={cliente} />
				</Modal>
			)}
		</div>
	);
};

export default ModalCreateReunion;
